import React from "react";
import { useParams } from "react-router-dom";
import { tr } from "@/data/translations";
import { useLang } from "@/contexts/LanguageContext";
import { SITE } from "@/data/site";
import { getAreaBySlug } from "@/data/serviceAreas";

/**
 * Brand-locked 1200×630 Open Graph card.
 * Rendered at /og-render/:lang/:slug for offline screenshot capture into /public/og/.
 * Lang from URL overrides app state so each card renders deterministically.
 */
const ROUTE_FOR_SLUG = {
  home: "/",
  about: "/about",
  services: "/services",
  washer: "/services/washer",
  dryers: "/services/dryers",
  refrigerator: "/services/refrigerator",
  dishwasher: "/services/dishwasher",
  "range-oven": "/services/range-oven",
  contact: "/contact",
  blog: "/blog",
  "hawaii-county": "/service-areas/hawaii-county",
  "kailua-kona": "/service-areas/kailua-kona",
  "waimea": "/service-areas/waimea",
  "captain-cook": "/service-areas/captain-cook",
};

const SHORT_LABEL = {
  en: {
    home: "Premium Appliance Repair",
    about: "Our Story",
    services: "Our Services",
    washer: "Washer Repair",
    dryers: "Dryer Repair",
    refrigerator: "Refrigerator Repair",
    dishwasher: "Dishwasher Repair",
    "range-oven": "Range & Oven Repair",
    contact: "Book a Service",
    blog: "Field Notes & Tips",
    "hawaii-county": "Serving Hawaii County",
    "kailua-kona": "Repair in Kailua-Kona",
    "waimea": "Repair in Waimea, HI",
    "captain-cook": "Repair in Captain Cook",
  },
  es: {
    home: "Reparación Premium de Electrodomésticos",
    about: "Nuestra Historia",
    services: "Nuestros Servicios",
    washer: "Reparación de Lavadoras",
    dryers: "Reparación de Secadoras",
    refrigerator: "Reparación de Refrigeradores",
    dishwasher: "Reparación de Lavavajillas",
    "range-oven": "Reparación de Estufas y Hornos",
    contact: "Reservar Servicio",
    blog: "Notas y Consejos",
    "hawaii-county": "Servicio en el Condado de Hawái",
    "kailua-kona": "Reparación en Kailua-Kona",
    "waimea": "Reparación en Waimea, HI",
    "captain-cook": "Reparación en Captain Cook",
  },
};

const OgRender = () => {
  const params = useParams();
  const { setLang } = useLang();
  const lang = params.lang === "es" ? "es" : "en";
  const slug = params.slug || "home";

  React.useEffect(() => {
    setLang(lang);
    document.body.style.background = "#FAFAFA";
    document.body.style.margin = "0";
  }, [lang, setLang]);

  const route = ROUTE_FOR_SLUG[slug] || "/";
  const area = getAreaBySlug(slug);
  const seo = area
    ? area.seo[lang]
    : tr(lang, `seo.${route}`) || tr(lang, "seo./");
  const headline = (SHORT_LABEL[lang] || SHORT_LABEL.en)[slug] || seo.title;
  const description = seo.description;

  return (
    <div
      data-testid="og-card"
      style={{
        width: "1200px",
        height: "630px",
        position: "relative",
        background: "#FAFAFA",
        color: "#111111",
        fontFamily: "'Outfit', system-ui, sans-serif",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Decorative blobs */}
      <div
        style={{
          position: "absolute",
          right: -120,
          top: -120,
          width: 520,
          height: 520,
          borderRadius: "9999px",
          background: "#DCE3DE",
          filter: "blur(0px)",
          opacity: 0.7,
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 80,
          bottom: -200,
          width: 460,
          height: 460,
          borderRadius: "9999px",
          background: "rgba(8,33,51,0.08)",
          filter: "blur(20px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 60,
          top: 60,
          width: 360,
          height: 360,
          borderRadius: "62% 38% 46% 54% / 52% 46% 54% 48%",
          background:
            "linear-gradient(135deg, #082133 0%, #0A2D47 50%, #082133 100%)",
          boxShadow: "0 40px 80px -30px rgba(8,33,51,0.35)",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "relative",
          padding: "80px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Top: brand */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              position: "relative",
              width: 60,
              height: 60,
              borderRadius: 16,
              background: "#082133",
              color: "#FAFAFA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 32,
              letterSpacing: "-0.02em",
            }}
          >
            G
            <span
              style={{
                position: "absolute",
                top: -3,
                right: -3,
                width: 12,
                height: 12,
                borderRadius: "9999px",
                background: "#FAFAFA",
                boxShadow: "0 0 0 3px #082133",
              }}
            />
          </div>
          <div style={{ lineHeight: 1.05 }}>
            <div style={{ fontWeight: 700, fontSize: 28, color: "#111111" }}>
              Genesis
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#4B5563",
                fontWeight: 600,
              }}
            >
              Appliance Repair
            </div>
          </div>
        </div>

        {/* Middle: eyebrow + headline */}
        <div style={{ maxWidth: 740 }}>
          <span
            style={{
              display: "inline-block",
              padding: "8px 16px",
              borderRadius: 999,
              background: "#FFFFFF",
              border: "1px solid rgba(8,33,51,0.12)",
              color: "#082133",
              fontSize: 13,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            {SITE.region}
          </span>
          <h1
            style={{
              margin: "26px 0 0 0",
              fontSize: 76,
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              fontWeight: 700,
              color: "#111111",
              maxWidth: 720,
            }}
          >
            {headline}
          </h1>
          <p
            style={{
              margin: "24px 0 0 0",
              fontFamily: "'Manrope', system-ui, sans-serif",
              fontSize: 22,
              lineHeight: 1.4,
              color: "#4B5563",
              maxWidth: 640,
              fontWeight: 500,
            }}
          >
            {description}
          </p>
        </div>

        {/* Bottom row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 14,
              padding: "16px 26px",
              borderRadius: 999,
              background: "#082133",
              color: "#FAFAFA",
              fontFamily: "'Manrope', system-ui, sans-serif",
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              boxShadow: "0 20px 40px -15px rgba(8,33,51,0.45)",
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                background: "#FAFAFA",
              }}
            />
            {SITE.phone}
          </div>
          <div
            style={{
              fontFamily: "'Manrope', system-ui, sans-serif",
              fontSize: 14,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#4B5563",
              fontWeight: 600,
              textAlign: "right",
            }}
          >
            genesisappliancerepair.com
            <div
              style={{
                marginTop: 4,
                color: "#082133",
                fontSize: 12,
              }}
            >
              {lang === "es" ? "Servicio Familiar · Isla Grande" : "Family-Owned · Big Island"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OgRender;
